// workspaceService.js
// Service to manage multiple workspaces

const WORKSPACE_STORAGE_KEY = 'busin.us-workspaces';

// Workspace structure
// {
//   id: string,
//   name: string,
//   nodes: [],
//   edges: [],
//   groups: [],
//   createdAt: Date,
//   lastModified: Date
// }

export class WorkspaceService {
  constructor() {
    this.workspaces = this.loadWorkspacesFromStorage();
  }

  // Load workspaces from localStorage
  loadWorkspacesFromStorage() {
    try {
      const savedWorkspaces = localStorage.getItem(WORKSPACE_STORAGE_KEY);
      if (savedWorkspaces) {
        const parsed = JSON.parse(savedWorkspaces);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error("Failed to load workspaces from localStorage", e);
    }
    return [];
  }

  // Save workspaces to localStorage
  saveWorkspacesToStorage() {
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(this.workspaces));
    } catch (e) {
      console.error("Failed to save workspaces to localStorage", e);
    }
  }

  // Get all workspaces
  getAllWorkspaces() {
    return [...this.workspaces];
  }

  // Get a workspace by ID
  getWorkspaceById(id) {
    return this.workspaces.find(workspace => workspace.id === id);
  }

  // Create a new workspace
  createWorkspace(name, workflowData = null) {
    const newWorkspace = {
      id: `workspace_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name || `Workspace ${this.workspaces.length + 1}`,
      nodes: workflowData?.nodes || [],
      edges: workflowData?.edges || [],
      groups: workflowData?.groups || [],
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    this.workspaces.push(newWorkspace);
    this.saveWorkspacesToStorage();
    return newWorkspace;
  }

  // Update a workspace
  updateWorkspace(id, updates) {
    const workspaceIndex = this.workspaces.findIndex(workspace => workspace.id === id);
    if (workspaceIndex !== -1) {
      this.workspaces[workspaceIndex] = {
        ...this.workspaces[workspaceIndex],
        ...updates,
        lastModified: new Date().toISOString()
      };
      this.saveWorkspacesToStorage();
      return this.workspaces[workspaceIndex];
    }
    return null;
  }

  // Delete a workspace
  deleteWorkspace(id) {
    this.workspaces = this.workspaces.filter(workspace => workspace.id !== id);
    this.saveWorkspacesToStorage();
  }

  // Import workflow to a new workspace
  importToNewWorkspace(name, workflowData) {
    return this.createWorkspace(name, workflowData);
  }

  // Export a workspace
  exportWorkspace(id) {
    const workspace = this.getWorkspaceById(id);
    if (workspace) {
      return {
        ...workspace
      };
    }
    return null;
  }
}

// Create a singleton instance
const workspaceService = new WorkspaceService();

export default workspaceService;